import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useToast } from "@packsize/toast";
import { useIdentityClient } from "@packsize/authentication";

import {
	createExternalAccessSubscription,
	type CreateExternalAccessSubscriptionParams,
	type DeleteExternalAccessSubscriptionParams,
	deleteExternalAccessSubscription,
	externalAccessSubscriptionKey,
	getExternalAccessSubscriptions,
} from "@/api/external-access-subscriptions-api";

export const useExternalAccessSubscriptions = (tenantId: string | undefined) => {
	const { client } = useIdentityClient();
	const queryFn = () => getExternalAccessSubscriptions(client);

	return useQuery({
		queryKey: [...externalAccessSubscriptionKey, tenantId],
		queryFn,
		enabled: !!tenantId,
	});
};

export const useCreateExternalAccessSubscription = (tenantId: string | undefined) => {
	const { client } = useIdentityClient();
	const queryClient = useQueryClient();
	const intl = useIntl();
	const { toastError } = useToast();

	return useMutation({
		mutationFn: (params: CreateExternalAccessSubscriptionParams) => createExternalAccessSubscription(client, params),
		onSuccess: async () => {
			if (tenantId) {
				await queryClient.invalidateQueries({
					queryKey: externalAccessSubscriptionKey,
				});
			}
		},
		onError: () => {
			toastError({
				message: intl.formatMessage({
					id: "Failed to generate API key",
					defaultMessage: "Failed to generate API key",
				}),
			});
		},
	});
};

export const useDeleteExternalAccessSubscription = (tenantId: string | undefined) => {
	const { client } = useIdentityClient();
	const queryClient = useQueryClient();
	const intl = useIntl();
	const { toastSuccess, toastError } = useToast();

	return useMutation({
		mutationFn: (params: DeleteExternalAccessSubscriptionParams) => deleteExternalAccessSubscription(client, params),
		onSuccess: async () => {
			if (tenantId) {
				await queryClient.invalidateQueries({
					queryKey: externalAccessSubscriptionKey,
				});
			}
			toastSuccess({
				message: intl.formatMessage({
					id: "API key deleted successfully",
					defaultMessage: "API key deleted successfully",
				}),
			});
		},
		onError: () => {
			toastError({
				message: intl.formatMessage({
					id: "Failed to delete API key",
					defaultMessage: "Failed to delete API key",
				}),
			});
		},
	});
};
